import { Link } from 'react-router';
import { useLocation } from 'react-router';
import { useTranslations } from '@/i18n/use-locale';
import { KadirMark } from '@/components/kadir-mark';
import { ADMIN_NAV, activeNavItem, visibleAdminNav } from '@/components/admin/nav';
import { formatCount } from '@/lib/format';
import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';

export interface AdminSidebarProps {
  /**
   * Live pending-work counts, rendered as badges. Supplied by the layout from the API on
   * every render — a badge is a claim about a queue somebody is working, so a stale one
   * is worse than none (C5).
   */
  badges?: Partial<Record<'verification' | 'documents', number>>;
  user: { name: string; role: string; initials: string };
  /**
   * The signed-in admin's grant, which decides what appears.
   *
   * The *permissions* cross the server/client boundary, not the filtered nav: every item
   * carries a Lucide component in `icon`, and a React component cannot be serialized
   * into a client component's props. Filtering here — against the same `ADMIN_NAV` the
   * layout's route check reads — keeps one source of truth without shipping the table
   * across the wire twice.
   *
   * Undefined means "show everything", which is what the component gallery wants.
   */
  permissions?: readonly string[];
}

/**
 * The fixed sidebar — 256px, pinned to the inline start, which under `dir="rtl"` is the
 * right-hand edge the design calls for.
 *
 * `start-0` and `border-e` rather than the prototype's `right-0` and `border-l`: same
 * pixels in Arabic, and the panel moves to the left edge with its border on the correct
 * side the moment `en` is selected, instead of detaching from the content.
 *
 * Entries an admin cannot open are absent rather than disabled. A greyed-out row invites
 * a click and teaches nothing; the refusal, when someone reaches the URL another way, is
 * the layout's job.
 */
export function AdminSidebar({ badges = {}, user, permissions }: AdminSidebarProps) {
  // Hidden below `lg`, where the mobile drawer (`AdminMobileNav`) carries the
  // same nav — so the console is usable at 768px instead of a 256px panel
  // crowding the content off the screen.
  return (
    <aside className="fixed inset-y-0 start-0 z-40 hidden w-64 flex-col border-e border-conc-100 bg-white lg:flex">
      <SidebarBody badges={badges} user={user} permissions={permissions} />
    </aside>
  );
}

/**
 * The sidebar's contents — brand, nav, signed-in admin — rendered by both the
 * fixed desktop `<aside>` and the mobile drawer, so the two cannot drift.
 */
export function SidebarBody({ badges = {}, user, permissions }: AdminSidebarProps) {
  const t = useTranslations('admin.nav');
  const brand = useTranslations('brand');
  const pathname = useLocation().pathname;
  const active = activeNavItem(pathname);
  const groups = permissions ? visibleAdminNav(permissions) : ADMIN_NAV;

  return (
    <>
      <div className="flex items-center gap-3 border-b border-conc-100 px-5 py-4">
        <KadirMark title={brand('fullName')} />
        <div className="leading-tight">
          <p className="text-lg font-bold text-navy">{brand('name')}</p>
          <p className="text-[11px] font-semibold tracking-wide text-conc-500">
            {brand('adminWordmark')}
          </p>
        </div>
      </div>

      <nav aria-label={t('label')} className="flex-1 overflow-y-auto px-3 py-4">
        {groups.map((group) => (
          <div key={group.key} className="mb-5">
            <p className="mb-2 px-3 text-[11px] font-bold text-conc-300">
              {t(`sections.${group.key}`)}
            </p>
            <ul className="space-y-1">
              {group.items.map((item) => {
                const isActive = active?.key === item.key;
                const badge = item.badgeKey ? badges[item.badgeKey] : undefined;

                return (
                  <li key={item.key}>
                    <Link
                      to={item.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        interactive,
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start text-[13.5px] font-semibold',
                        isActive
                          ? 'bg-pitch text-white shadow-sm'
                          : 'text-conc-600 hover:bg-pitch-50 hover:text-pitch-800',
                      )}
                    >
                      <item.icon
                        aria-hidden
                        className="h-[18px] w-[18px] shrink-0"
                        strokeWidth={1.9}
                      />
                      <span className="flex-1">{t(`items.${item.key}`)}</span>
                      {badge ? (
                        <span
                          className={cn(
                            'num rounded-full px-1.5 py-0.5 text-[11px] font-bold',
                            isActive ? 'bg-white/20 text-white' : 'bg-gold-50 text-gold-700',
                          )}
                        >
                          {formatCount(badge)}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-conc-100 p-3">
        <div className="flex items-center gap-3 rounded-xl bg-sideline p-3">
          <span
            aria-hidden
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy text-[12px] font-bold text-white"
          >
            {user.initials}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[13px] font-bold text-navy">{user.name}</p>
            <p className="truncate text-[11px] text-conc-500">{user.role}</p>
          </div>
        </div>
      </div>
    </>
  );
}
